import { Navigate, Route, useLocation, useSearchParams } from '@solidjs/router'
import { lazy, Match, Show, Switch } from 'solid-js'
import { Page } from './components/shell.tsx'
import { ViewSelector } from './components/view-selector.tsx'

const BASE = '/prototype'
const Shell = lazy(() =>
  import('./components/shell.tsx').then((module) => ({ default: module.Shell })),
)
const Departments = lazy(() => import('./screens/departments.tsx'))
const Home = lazy(() => import('./screens/home.tsx'))
const Inbox = lazy(() => import('./screens/inbox.tsx'))
const Customers = lazy(() => import('./screens/customers.tsx'))
const Customer = lazy(() => import('./screens/customer.tsx'))
const Orders = lazy(() => import('./screens/orders.tsx'))
const ProposalPrint = lazy(() => import('./screens/proposal-print.tsx'))
const Order = lazy(() => import('./screens/order.tsx'))
const ProductionOrders = lazy(() => import('./screens/production-orders.tsx'))
const ProductionQueue = lazy(() => import('./screens/production-queue.tsx'))
const ProductionPlan = lazy(() => import('./screens/production-plan.tsx'))
const ProductionOrder = lazy(() => import('./screens/production-order.tsx'))
const Engineering = lazy(() => import('./screens/engineering.tsx'))
const EngineeringTask = lazy(() => import('./screens/engineering-task.tsx'))
const ProductionScrap = lazy(() => import('./screens/production-scrap.tsx'))
const ProductionNesting = lazy(() => import('./screens/production-nesting.tsx'))
const NestingImportButton = lazy(() =>
  import('./components/nesting-import.tsx').then((module) => ({
    default: module.NestingImportButton,
  })),
)
const Equipment = lazy(() => import('./screens/equipment.tsx'))
const EquipmentMachine = lazy(() => import('./screens/equipment-machine.tsx'))
const Warehouse = lazy(() => import('./screens/warehouse.tsx'))
const WarehouseMaterial = lazy(() => import('./screens/warehouse-material.tsx'))
const WarehousePurchasing = lazy(() => import('./screens/warehouse-purchasing.tsx'))
const Suppliers = lazy(() => import('./screens/suppliers.tsx'))
const Supplier = lazy(() => import('./screens/supplier.tsx'))
const Logistics = lazy(() => import('./screens/logistics.tsx'))
const FinanceInvoices = lazy(() => import('./screens/finance-invoices.tsx'))
const FinancePaymentRequests = lazy(() => import('./screens/finance-payment-requests.tsx'))
const Catalog = lazy(() => import('./screens/catalog.tsx'))
const CatalogProduct = lazy(() => import('./screens/catalog-product.tsx'))
const Documents = lazy(() => import('./screens/documents.tsx'))
const Users = lazy(() => import('./screens/users.tsx'))
const Settings = lazy(() => import('./screens/settings.tsx'))

type Workspace = 'orders' | 'departments' | 'nesting'
const ProductionWorkspace = () => {
  const [params, setParams] = useSearchParams<{ workspace?: string }>()
  const workspace = (): Workspace =>
    params.workspace === 'departments' || params.workspace === 'nesting'
      ? params.workspace
      : 'orders'
  return (
    <Page
      crumbs={[{ label: 'Виробничі замовлення' }]}
      tabs={
        <ViewSelector
          label="Робочий простір виробництва"
          value={workspace()}
          options={[
            { key: 'orders', label: 'Замовлення' },
            { key: 'departments', label: 'Відділи' },
            { key: 'nesting', label: 'Карти розкрою' },
          ]}
          onChange={(key) => setParams({ workspace: key === 'orders' ? undefined : key })}
        />
      }
      actions={
        <Show when={workspace() !== 'nesting'}>
          <NestingImportButton size="sm" />
        </Show>
      }
    >
      <Switch fallback={<ProductionOrders />}>
        <Match when={workspace() === 'departments'}>
          <Departments />
        </Match>
        <Match when={workspace() === 'nesting'}>
          <ProductionNesting />
        </Match>
      </Switch>
    </Page>
  )
}

const LegacyDepartments = () => {
  const location = useLocation()
  const href = () => {
    const query = new URLSearchParams(location.search)
    query.set('workspace', 'departments')
    return `${BASE}/production?${query}`
  }
  return <Navigate href={href()} />
}

export const prototypeRoutes = () => (
  <>
    <Route path={`${BASE}/orders/:id/proposals/:proposalId/print`} component={ProposalPrint} />
    <Route path={BASE} component={Shell}>
      <Route path="/" component={() => <Navigate href={`${BASE}/home`} />} />
      <Route path="/departments" component={LegacyDepartments} />
      <Route path="/home" component={Home} />
      <Route path="/inbox" component={Inbox} />
      <Route path="/customers" component={Customers} />
      <Route path="/customers/:id" component={Customer} />
      <Route path="/orders" component={Orders} />
      <Route path="/orders/:id" component={Order} />
      <Route path="/production" component={ProductionWorkspace} />
      <Route path="/production/queue" component={ProductionQueue} />
      <Route path="/production/plan" component={ProductionPlan} />
      <Route path="/production/scrap" component={ProductionScrap} />
      <Route path="/warehouse" component={Warehouse} />
      <Route path="/warehouse/purchasing" component={WarehousePurchasing} />
      <Route path="/warehouse/:id" component={WarehouseMaterial} />
      <Route path="/suppliers" component={Suppliers} />
      <Route path="/suppliers/:id" component={Supplier} />
      <Route path="/logistics" component={Logistics} />
      <Route path="/finance/invoices" component={FinanceInvoices} />
      <Route path="/finance/payment-requests" component={FinancePaymentRequests} />
      <Route path="/catalog" component={Catalog} />
      <Route path="/catalog/:id" component={CatalogProduct} />
      <Route path="/documents" component={Documents} />
      <Route path="/users" component={Users} />
      <Route path="/settings" component={Settings} />
      <Route path="/equipment" component={Equipment} />
      <Route path="/equipment/:id" component={EquipmentMachine} />
      <Route path="/production/:id" component={ProductionOrder} />
      <Route path="/engineering" component={Engineering} />
      <Route path="/engineering/:id" component={EngineeringTask} />
      <Route path="*" component={() => <Navigate href={`${BASE}/home`} />} />
    </Route>
  </>
)
